import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ConfirmModal from '../Common/ConfirmModal';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import {
  EMPTY_GENERATE_COPY,
  FIGHT_COPY,
  FIGHT_EDIT_LABEL,
  FIGHT_KEEP_NOTE,
  FIGHT_KEEP_TALK,
  FILL_EMPTY_HINT,
  generateDisclaimer,
  createGenerateWait,
  GENERATE_CANCEL_LABEL,
  GENERATE_CANCELLED_COPY,
  GENERATE_RETRY_LABEL,
  GENERATE_TIMED_OUT_COPY,
  generateErrorCopy,
  generateEtaCopy,
  generateFallbackCopy,
  finishGenerateNote,
  generateModeLine,
  generateProgressCopy,
  generateProgressPercent,
  gridHasOutline,
  outlineAlreadyExists,
  fillsRestCopy,
  outlineStillLoading,
  NOTES_HANDY_COPY,
  acceptOfferLabel,
  OFFER_EDIT_LABEL,
  OFFER_KEPT_NOTE,
  offerSkipKey,
  offerSkippedReasons,
  fillSkippedLines,
  missingSequencesLine,
  FILL_SKIPPED_HEADING,
  OFFER_REJECT_LABEL,
  offerScenes,
  offerSummary,
  offerSceneBeatLines,
  offerGrainLabel,
  offerSceneHeading,
  offerSequenceCounts,
  offerSequenceLabel,
  offerSequenceName,
  outlineVerdictPending,
  offerStubChildLabel,
  offerStubExtraBeats,
  groupOfferScenes,
  offerFightRows,
  offerFieldText,
  offerIntegrityNotes,
  editOfferScene,
  REPLACE_CONFIRM_TITLE,
  REPLACE_REVIEW_FIRST_NOTE,
  REPLACE_TYPE_LABEL,
  REPLACE_TYPE_WORD,
  proseLossFromError,
  proseLossLine,
  REPLACE_EXISTING_LABEL,
  replaceWarning,
  thinWarnCopy,
  workshopGenerateClosedCopy,
  USE_NOTES_LABEL,
  CONTINUE_INCOMPLETE_LABEL,
  ACCEPT_ANYWAY_LABEL,
  PENDING_OFFER_NOTE,
  PENDING_OFFER_RESUME,
  PENDING_OFFER_DISCARD,
  PENDING_OFFER_REPLACE_TITLE,
  PENDING_OFFER_REPLACE_CONFIRM,
  pendingOfferListLabel,
  pendingOfferReplaceCopy,
  pendingOfferReplaceNeeded,
  isPlaceholderScene,
  offerPlaceholderCount,
} from '../../utils/plottrackGenerate';
import {
  OFFER_PLACEHOLDER,
  OFFER_PLACEHOLDER_COUNT,
  EMPTY_GRID_TOOLS_WAIT,
} from '../../utils/writerCopy';
import {
  WHOLE_OUTLINE_SCOPE,
  clearPendingOffer,
  normalizeOfferScope,
  offerScopeFor,
  readPendingOffer,
  readPendingOffers,
  readUseNotes,
  writePendingOffer,
  writeUseNotes,
} from '../../utils/plottrackOfferPersist';
import PlotTrackCheckOutline from './PlotTrackCheckOutline';
import PlotTrackSequenceCheck from './PlotTrackSequenceCheck';
import PlotTrackGenerateConfirm from './PlotTrackGenerateConfirm';
import PlanPartConfirm from './PlanPartConfirm';

function OfferSceneBody({ scene, index, editing, nestedStub, onPatch }) {
  const beats = nestedStub ? offerStubExtraBeats(scene) : offerSceneBeatLines(scene);
  const name = offerFieldText(scene.name, offerGrainLabel(scene));
  const note = offerFieldText(scene.note);
  const placeholder = !nestedStub && isPlaceholderScene(scene);
  const grainLabel = offerGrainLabel(scene);
  const grainWord = grainLabel.toLowerCase();
  const readLabel = nestedStub ? offerStubChildLabel(scene) : offerSceneHeading(scene);
  return (
    <>
      {editing ? (
        <div className="pt-generate__offer-edit">
          <label>
            {grainLabel}
            <input
              aria-label={`Edit ${grainWord} ${index + 1}`}
              value={name}
              onChange={(e) => onPatch({ name: e.target.value })}
            />
          </label>
          <label>
            Note
            <textarea
              className="pt-generate__offer-note-input"
              aria-label={`Edit note for ${grainWord} ${index + 1}`}
              value={note}
              onChange={(e) => onPatch({ note: e.target.value })}
            />
          </label>
        </div>
      ) : (
        <span>{readLabel}</span>
      )}
      {beats.length > 0 && (
        <ul className="pt-generate__offer-beats">
          {beats.map((beat, beatIndex) => (
            <li key={`${beatIndex}-${beat}`}>{beat}</li>
          ))}
        </ul>
      )}
      {!editing && note ? <p className="pt-generate__offer-note">{note}</p> : null}
      {!editing && placeholder ? <p className="pt-generate__offer-placeholder">{OFFER_PLACEHOLDER}</p> : null}
    </>
  );
}

function compareOfferScopes(a, b) {
  if (a === WHOLE_OUTLINE_SCOPE) return 1;
  if (b === WHOLE_OUTLINE_SCOPE) return -1;
  return a.localeCompare(b);
}

function previewScopeFor(partName) {
  return offerScopeFor({
    partName,
    whole: !String(partName || '').trim(),
  });
}

function readOfferView(storyId, columns, startGenerate) {
  const pending = readPendingOffers(storyId);
  const entries = Object.entries(pending);
  const restore = Boolean(
    storyId
    && !startGenerate
    && !gridHasOutline(columns || [])
    && entries.length === 1,
  );
  return {
    pending,
    offer: restore ? entries[0][1] : null,
    scope: restore ? entries[0][0] : null,
  };
}

/**
 * One generate on the writer-facing grid (#827 / #1055).
 * Soft warn when thin. Always lands as Accept / edit / reject.
 * Accept fills empty spots only when scenes are already on the line.
 * A thread row with no scenes is a first outline: Accept writes, and
 * Replace existing stays hidden until scene columns exist.
 * Use Notes for Generate steers only when a Framework is chosen.
 * Waiting drafts are per sequence (#2012). Whole-outline Generate is its own scope.
 */
export default function PlotTrackGenerate({
  board,
  startGenerate = false,
  handoffId = '',
  bookPath = null,
  bookPathStatus = 'idle',
  quietUntilScene = false,
  planPart = null,
  onPlanPartCancel,
  onPlanPartStart,
  onResumePending = null,
  onOutcome = null,
  onResult = null,
}) {
  const storyId = board.storyId || board.story_id || null;
  const [confirmOpen, setConfirmOpen] = useState(false);
  // What Replace would delete, and which write asked (#replace-prose).
  const [proseLoss, setProseLoss] = useState(null);
  const [confirmApply, setConfirmApply] = useState('replace');
  const [replaceIntent, setReplaceIntent] = useState(false);
  const [seqRunSignal] = useState(0);
  const [costOpen, setCostOpen] = useState(false);
  const [sizePending, setSizePending] = useState(Boolean(board.assess));
  const [busy, setBusy] = useState(false);
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState(null);
  const [emptyNote, setEmptyNote] = useState(board.empty_reason || null);
  const [live, setLive] = useState(null);
  const [finishedNote, setFinishedNote] = useState(null);
  // Run 11: what Add to my outline left out, and why. Never silent.
  const [skippedLines, setSkippedLines] = useState([]);
  const [missingLine, setMissingLine] = useState('');
  const [useNotes, setUseNotes] = useState(() => readUseNotes(storyId));
  // A handoff, and a book that already has scenes, keep the draft behind Resume
  // so Replace still opens the cost dialog. An empty grid with one draft shows it.
  const [boot] = useState(() => readOfferView(storyId, board.columns, startGenerate));
  const [offer, setOffer] = useState(boot.offer);
  const [activeScope, setActiveScope] = useState(boot.scope);
  const [pendingByScope, setPendingByScope] = useState(boot.pending);
  const [editing, setEditing] = useState(false);
  const [fightChoices, setFightChoices] = useState({});
  const [fightEdits, setFightEdits] = useState({});
  const [readiness, setReadiness] = useState(board.generate_readiness || null);
  // A passing re-check retires the mount-time "nothing to work from" line.
  // A later generate result can still set emptyNote and show it.
  const [gatePassed, setGatePassed] = useState(false);
  const openedHandoff = useRef('');
  const waitRef = useRef(null);
  const retryRef = useRef({ kind: 'preview' });
  const gateRecheckRef = useRef(false);
  const storyIdRef = useRef(storyId);
  storyIdRef.current = storyId;
  const activeScopeRef = useRef(boot.scope);
  const offerRef = useRef(boot.offer);
  offerRef.current = offer;
  const scopeReplaceRef = useRef(null);
  const [scopeReplace, setScopeReplace] = useState(null);
  // Cleared only after the writer agrees to replace this plan's waiting draft.
  const [planClearedFor, setPlanClearedFor] = useState('');
  const [planDismissed, setPlanDismissed] = useState(false);
  const planScope = planPart?.name ? previewScopeFor(planPart.name) : '';

  const columns = live ? live.columns : (board.columns || []);
  const hasGrid = columns.length > 0;
  const hasOutline = gridHasOutline(columns);
  const generated = live ? live.generated : board.generated;
  const hasFramework = Boolean(
    readiness?.has_framework
    || (board.framework_ref && String(board.framework_ref).toLowerCase() !== 'none'),
  );
  const modeLine = generateModeLine({
    useNotes,
    hasFramework,
    fillsSpine: readiness?.fills_spine === true,
    modeLine: readiness?.mode_line,
    hasTalk: readiness?.has_talk,
  });
  const fallbackCopy = generateFallbackCopy({ useNotes, hasFramework });
  const thin = readiness?.thin === true;
  const assessGate = Boolean(board.assess);
  const gatePending = assessGate && sizePending;
  const outlineExists = !outlineVerdictPending(readiness, gatePending)
    && outlineAlreadyExists(readiness, hasOutline, bookPath);
  const workshopClosedCopy = workshopGenerateClosedCopy(readiness, {
    pending: gatePending,
    hasOutline,
    bookPath,
    bookPathStatus,
  });
  // An outline or a still-loading check closes the whole-book Generate button.
  // Plot Workshop and a first chat do not. Plan this part has its own confirm.
  const outlineLoading = outlineStillLoading({
    hasOutline,
    readiness,
    bookPath,
    bookPathStatus,
    assessPending: gatePending,
  });
  const fillsRest = fillsRestCopy(readiness);
  // Replace stays offered on any outline, including a started one Generate can still fill.
  const showReplace = outlineExists || hasOutline;
  const replaceBlocked = !showReplace;
  const generateClosed = outlineLoading || outlineExists;
  const hideUntilScene = quietUntilScene && !hasOutline;
  const warn = outlineExists ? null : thinWarnCopy({
    thin,
    hole: readiness?.hole || {
      framework: !hasFramework,
      workshop: readiness?.has_workshop === false,
    },
  });

  useEffect(() => {
    setLive(null);
  }, [board.rows, board.columns]);

  useEffect(() => {
    if (hasGrid) setEmptyNote(null);
  }, [hasGrid]);

  useEffect(() => {
    if (!board.assess) {
      setSizePending(false);
      return undefined;
    }
    let cancelled = false;
    setSizePending(true);
    board.assess({
      use_notes: useNotes,
      part_name: planPart?.name || '',
    }).then((next) => {
      if (cancelled || !next) return;
      setReadiness(next.generate_readiness || next);
    }).catch(() => {}).finally(() => {
      // A click/handoff re-check owns sizePending while it is in flight.
      if (!cancelled && !gateRecheckRef.current) setSizePending(false);
    });
    return () => { cancelled = true; };
  }, [board.assess, useNotes, board.rows, board.columns, planPart?.name]);

  useEffect(() => () => {
    waitRef.current?.clear();
    waitRef.current = null;
    const resolve = scopeReplaceRef.current;
    scopeReplaceRef.current = null;
    resolve?.(false);
  }, []);

  useEffect(() => {
    if (!busy) return undefined;
    setElapsedMs(0);
    const started = Date.now();
    const id = setInterval(() => {
      setElapsedMs(Date.now() - started);
    }, 250);
    return () => clearInterval(id);
  }, [busy]);

  useEffect(() => {
    openedHandoff.current = '';
    setGatePassed(false);
    setUseNotes(readUseNotes(storyId));
    const view = readOfferView(storyId, board.columns, startGenerate);
    setPendingByScope(view.pending);
    setOffer(view.offer);
    offerRef.current = view.offer;
    activeScopeRef.current = view.scope;
    setActiveScope(view.scope);
    setEditing(false);
    setPlanClearedFor('');
    setPlanDismissed(false);
    const resolve = scopeReplaceRef.current;
    scopeReplaceRef.current = null;
    setScopeReplace(null);
    resolve?.(false);
  }, [storyId, startGenerate]);

  useEffect(() => {
    setPlanClearedFor('');
    setPlanDismissed(false);
  }, [planScope]);

  const rememberScope = (scope) => {
    activeScopeRef.current = scope;
    setActiveScope(scope);
  };

  const refreshPending = () => {
    setPendingByScope(readPendingOffers(storyIdRef.current));
  };

  const keepOffer = (nextOffer, scope) => {
    const key = nextOffer
      ? (scope != null && String(scope).trim() !== ''
        ? normalizeOfferScope(scope)
        : (activeScopeRef.current || offerScopeFor({ plan: nextOffer.plan })))
      : (scope != null && String(scope).trim() !== ''
        ? normalizeOfferScope(scope)
        : activeScopeRef.current);
    if (nextOffer) {
      writePendingOffer(storyId, nextOffer, key);
      setOffer(nextOffer);
      rememberScope(key);
    } else if (key) {
      clearPendingOffer(storyId, key);
      setOffer(null);
      rememberScope(null);
    } else {
      setOffer(null);
      rememberScope(null);
    }
    refreshPending();
  };

  const discardScope = (scope) => {
    const key = scope != null && String(scope).trim() !== ''
      ? normalizeOfferScope(scope)
      : null;
    if (key) clearPendingOffer(storyId, key);
    if (!key || key === activeScopeRef.current) {
      setReplaceIntent(false);
      setOffer(null);
      rememberScope(null);
      setEditing(false);
      setFightChoices({});
      setFightEdits({});
    }
    refreshPending();
  };

  const dropOffer = () => {
    discardScope(activeScopeRef.current);
  };

  const resumePending = (scope) => {
    const key = normalizeOfferScope(scope);
    const waiting = pendingByScope[key] || readPendingOffer(storyId, key);
    if (!waiting) return;
    setCostOpen(false);
    setOffer(waiting);
    rememberScope(key);
    setFightChoices({});
    setFightEdits({});
    setEditing(false);
    // Plan-part cancel closes the Build panel. Resume has to reopen it
    // after the stored outline is back, or the writer only sees it close.
    onResumePending?.();
  };

  const askScopeReplace = (scope) => new Promise((resolve) => {
    if (scopeReplaceRef.current) {
      resolve(false);
      return;
    }
    scopeReplaceRef.current = resolve;
    setScopeReplace(scope);
  });

  const finishScopeReplace = (ok) => {
    const resolve = scopeReplaceRef.current;
    scopeReplaceRef.current = null;
    setScopeReplace(null);
    resolve?.(ok);
  };

  const liveScope = () => (offerRef.current ? activeScopeRef.current : null);

  const scopeDraftMap = (scope, { includeStorage = false } = {}) => {
    const map = { ...pendingByScope };
    const live = liveScope();
    if (live && offerRef.current) {
      const liveKey = normalizeOfferScope(live);
      if (!map[liveKey]) map[liveKey] = offerRef.current;
    }
    if (includeStorage) {
      const key = normalizeOfferScope(scope);
      const stored = readPendingOffer(storyIdRef.current, key);
      if (stored) map[key] = stored;
    }
    return map;
  };

  const needsReplace = (scope, options) => pendingOfferReplaceNeeded(
    scopeDraftMap(scope, options),
    scope,
    liveScope(),
  );

  // #2012: confirm a same-scope replace before the cost dialog. Round 1
  // asked only after that dialog was already confirmed, so the warning
  // never appeared in the estimate the writer actually saw.
  const beginPaid = (scope, openDialog) => {
    if (!needsReplace(scope, { includeStorage: true })) {
      openDialog();
      return;
    }
    askScopeReplace(scope).then((ok) => {
      if (ok) openDialog();
    });
  };

  const startPreview = (args) => {
    const partName = args.fromPlan ? (args.partName || '') : '';
    const scope = previewScopeFor(partName);
    const run = () => {
      if (args.fromPlan) onPlanPartStart?.();
      runPreview({ ...args, partName, fromPlan: Boolean(args.fromPlan) });
    };
    if (args.replaceConfirmed || !needsReplace(scope, { includeStorage: true })) {
      run();
      return;
    }
    askScopeReplace(scope).then((ok) => {
      if (ok) run();
    });
  };

  const onUseNotesChange = (checked) => {
    setUseNotes(checked);
    writeUseNotes(storyId, checked);
  };

  const fightResolutions = useMemo(() => {
    return offerFightRows(offer).map((fight) => {
      const name = offerFieldText(fight.name);
      return {
        name,
        choice: fightChoices[name] || '',
        text: fightEdits[name] || offerFieldText(fight.note_text),
      };
    }).filter((row) => row.choice);
  }, [offer, fightChoices, fightEdits]);

  const applyLive = (next) => {
    if (!next) return;
    setLive({
      rows: next.rows || [],
      columns: next.columns || [],
      generated: next.generated === true,
      disclaimer: next.disclaimer,
      series_background: next.series_background === true,
    });
    if (next.generate_readiness) setReadiness(next.generate_readiness);
    const nextHasGrid = (next?.columns || []).length > 0;
    if (nextHasGrid) {
      setEmptyNote(null);
    } else if (next?.empty_reason || (next?.wrote === false && next?.generated !== true && !next?.offer)) {
      setEmptyNote(next.empty_reason || EMPTY_GENERATE_COPY);
    } else if (next?.generated === true || next?.wrote === true) {
      setEmptyNote(null);
    }
  };

  const beginWait = () => {
    waitRef.current?.clear();
    const wait = createGenerateWait();
    waitRef.current = wait;
    return wait;
  };

  const endWait = (wait) => {
    wait?.clear();
    if (waitRef.current === wait) waitRef.current = null;
  };

  const failWait = (err) => {
    if (err?.name === 'GenerateTimeoutError') setError(GENERATE_TIMED_OUT_COPY);
    else if (err?.name === 'GenerateCancelError') setError(GENERATE_CANCELLED_COPY);
    else setError(generateErrorCopy(err));
    onResult?.();
  };

  const onRetry = () => {
    const job = retryRef.current || { kind: 'preview' };
    setError(null);
    if (job.kind === 'continue') {
      runContinue();
      return;
    }
    if (job.kind === 'accept') {
      runAccept(job.applyMode, { forceUngrounded: job.forceUngrounded });
      return;
    }
    setCostOpen(true);
  };

  const runPreview = async ({ steerNote = '', partName = '', fromPlan = false } = {}) => {
    if (!fromPlan) {
      if (replaceIntent) {
        if (!showReplace) return;
      } else if (generateClosed) return;
    }
    const scope = previewScopeFor(partName);
    setConfirmOpen(false);
    setBusy(true);
    setError(null);
    setFinishedNote(null);
    setOffer(null);
    setEditing(false);
    retryRef.current = { kind: 'preview' };
    const wait = beginWait();
    try {
      const next = await wait.race(board.generate({
        use_notes: useNotes,
        steer_note: steerNote,
        part_name: partName,
        signal: wait.signal,
      }));
      if (next == null) return;
      if (next?.offer) {
        keepOffer(next.offer, scope);
        setFightChoices({});
        setFightEdits({});
        if (next.generate_readiness) setReadiness(next.generate_readiness);
        if (next.empty_reason && !(next.columns || []).length) {
          setEmptyNote(next.empty_reason);
        }
        onResult?.();
        return;
      }
      applyLive(next);
      const finished = finishGenerateNote(next);
      if (finished.error) setError(finished.error);
      setFinishedNote(finished.note);
      onResult?.();
    } catch (err) {
      failWait(err);
    } finally {
      endWait(wait);
      setBusy(false);
    }
  };

  const runContinue = async () => {
    if (generateClosed) return;
    const ok = await askPaid('plot_continue');
    if (!ok) return;
    setBusy(true);
    setError(null);
    setFinishedNote(null);
    retryRef.current = { kind: 'continue' };
    const wait = beginWait();
    try {
      const next = await wait.race(board.generate({
        use_notes: useNotes,
        continue_incomplete: true,
        plan: offer?.plan,
        signal: wait.signal,
      }));
      if (next == null) return;
      if (next?.offer) {
        keepOffer(next.offer, activeScopeRef.current);
        setFightChoices({});
        setFightEdits({});
        if (next.generate_readiness) setReadiness(next.generate_readiness);
        onResult?.();
        return;
      }
      applyLive(next);
      const finished = finishGenerateNote(next);
      if (finished.error) setError(finished.error);
      setFinishedNote(finished.note);
      onResult?.();
    } catch (err) {
      failWait(err);
    } finally {
      endWait(wait);
      setBusy(false);
    }
  };

  const openReplaceConfirm = (loss, applyMode = 'replace') => {
    setProseLoss(loss && loss.drafted ? loss : null);
    setConfirmApply(applyMode);
    setConfirmOpen(true);
  };

  const runAccept = async (applyMode, { forceUngrounded = false, confirmProse = false } = {}) => {
    const snapshot = offer;
    const scope = activeScopeRef.current || offerScopeFor({ plan: snapshot?.plan });
    const restoreWaiting = () => {
      if (!snapshot) return;
      writePendingOffer(storyId, snapshot, scope);
      refreshPending();
    };
    // Drop this scope before the request returns. A remount during Accept
    // must not bring this draft back. Other sequences stay waiting.
    clearPendingOffer(storyId, scope);
    setPendingByScope((prev) => {
      const next = { ...prev };
      delete next[normalizeOfferScope(scope)];
      return next;
    });
    setConfirmOpen(false);
    setBusy(true);
    setError(null);
    setFinishedNote(null);
    setSkippedLines([]);
    setMissingLine('');
    retryRef.current = { kind: 'accept', applyMode, forceUngrounded };
    const wait = beginWait();
    try {
      const accept = board.acceptGenerate || board.generate;
      const next = await wait.race(accept({
        use_notes: useNotes,
        accept: true,
        apply_mode: applyMode,
        replace: applyMode === 'replace',
        plan: offer?.plan,
        fight_resolutions: fightResolutions,
        force_accept_ungrounded: forceUngrounded || offer?.entity_grounding?.dominated === true,
        confirm_prose_loss: confirmProse,
        signal: wait.signal,
      }));
      if (next == null) {
        restoreWaiting();
        return;
      }
      dropOffer();
      applyLive(next);
      const finished = finishGenerateNote(next);
      if (finished.error) setError(finished.error);
      setFinishedNote(finished.note);
      onResult?.();
      setSkippedLines(fillSkippedLines(next?.fill_skipped));
      setMissingLine(missingSequencesLine(next?.missing_sequences));
    } catch (err) {
      restoreWaiting();
      // The server found drafted prose this write would delete: ask, with its counts.
      const loss = proseLossFromError(err);
      if (loss) openReplaceConfirm(loss, applyMode);
      else failWait(err);
    } finally {
      endWait(wait);
      setBusy(false);
    }
  };

  const showNothingToWorkFrom = () => {
    setCostOpen(false);
    setGatePassed(false);
    setEmptyNote(board.empty_reason || EMPTY_GENERATE_COPY);
  };

  // Mount assess can finish before the writer sends a chat message.
  // has_gate=false from that pass is not safe to trust on the next click.
  const recheckGate = async () => {
    if (!board.assess || gateRecheckRef.current) return { skipped: true };
    const storyAtCall = storyIdRef.current;
    gateRecheckRef.current = true;
    setSizePending(true);
    try {
      const next = await board.assess({
        use_notes: useNotes,
        part_name: planPart?.name || '',
      });
      if (storyIdRef.current !== storyAtCall) return { stale: true };
      if (!next) return { failed: true };
      const fresh = next.generate_readiness || next;
      setReadiness(fresh);
      return { fresh };
    } catch {
      if (storyIdRef.current !== storyAtCall) return { stale: true };
      return { failed: true };
    } finally {
      gateRecheckRef.current = false;
      setSizePending(false);
    }
  };

  const noteGatePassed = () => {
    setGatePassed(true);
    setEmptyNote(null);
  };

  const openCostDialog = async ({ forReplace = false } = {}) => {
    if (busy || costOpen || gateRecheckRef.current) return;
    if (forReplace) {
      if (!showReplace) return;
    } else if (generateClosed) return;
    // Empty input is free to know. Ask for a cost only when there is something to read.
    // Re-run the free check once before trusting a mount-time has_gate=false.
    if (!hasGrid && readiness?.has_gate === false) {
      if (!board.assess) {
        showNothingToWorkFrom();
        return;
      }
      const result = await recheckGate();
      if (result.skipped || result.stale) return;
      if (result.fresh?.has_gate === true) {
        noteGatePassed();
        setCostOpen(true);
        return;
      }
      showNothingToWorkFrom();
      return;
    }
    setCostOpen(true);
  };

  const confirmPlanPart = (note) => {
    startPreview({
      steerNote: String(note || '').trim(),
      partName: planPart?.name || '',
      fromPlan: true,
      replaceConfirmed: true,
    });
  };

  const onClick = () => {
    if (generateClosed) return;
    setReplaceIntent(false);
    beginPaid(WHOLE_OUTLINE_SCOPE, () => openCostDialog());
  };

  const handoffKey = startGenerate ? String(handoffId || 'handoff') : '';
  useEffect(() => {
    if (!handoffKey || busy || offer || !board.generate) return;
    if (openedHandoff.current === handoffKey) return;
    // Wait until the check can say whether Generate is open. Marking the
    // handoff first used to skip the estimate on the next try.
    if (gatePending || generateClosed) return;
    openedHandoff.current = handoffKey;
    // The cost dialog opens from the Generate click. Opening the panel is not that click.
    // The same stale has_gate=false gets one free re-check before the nothing line.
    if (!hasGrid && readiness?.has_gate === false) {
      if (!board.assess) {
        showNothingToWorkFrom();
        return;
      }
      const storyAtStart = storyId;
      recheckGate().then((result) => {
        if (storyIdRef.current !== storyAtStart) return;
        if (result.skipped || result.stale) return;
        if (result.fresh?.has_gate === true) {
          noteGatePassed();
          return;
        }
        showNothingToWorkFrom();
      });
    }
  }, [
    handoffKey,
    storyId,
    board.generate,
    board.assess,
    board.empty_reason,
    busy,
    offer,
    gatePending,
    generateClosed,
    hasGrid,
    readiness?.has_gate,
    useNotes,
    planPart?.name,
  ]);

  const disclaimer = generated
    ? generateDisclaimer({
        disclaimer: live?.disclaimer || board.disclaimer,
        series_background: live?.series_background || board.series_background,
        generated: true,
      })
    : null;
  const emptyExplain = !hasGrid
    ? (emptyNote || (gatePassed ? null : board.empty_reason) || null)
    : null;
  const outcomeText = error || finishedNote || emptyExplain || '';
  const outcomeKind = error ? 'error' : (finishedNote ? 'done' : 'empty');
  useEffect(() => {
    onOutcome?.(outcomeText ? { text: outcomeText, kind: outcomeKind } : null);
  }, [onOutcome, outcomeText, outcomeKind]);
  const scenes = offerScenes(offer);
  // #1351: Add to my outline skips sequences the writer already approved.
  // The list still shows their scenes, so say so once per sequence.
  const keptSequences = new Set(hasOutline ? offer?.fill_preview?.kept_offer_sequences || [] : []);
  const keptNoted = new Set();
  // Run 46: name each sequence once above its scenes, with a count, so the writer can
  // see what lands where before accepting.
  const sequenceCounts = offerSequenceCounts(scenes);
  let lastSequence = null;
  const skippedReasons = hasOutline ? offerSkippedReasons(offer) : new Map();
  const planNeedsReplace = Boolean(planScope && needsReplace(planScope));
  const planReady = !planNeedsReplace || planClearedFor === planScope;
  const planWarning = Boolean(
    planScope && !busy && planNeedsReplace && !planDismissed && planClearedFor !== planScope,
  );
  const coverOpen = Boolean(costOpen || (planPart?.name && planReady && !planWarning));
  const listedOffers = { ...pendingByScope };
  if (offer && activeScope) {
    const shownKey = normalizeOfferScope(activeScope);
    if (!listedOffers[shownKey]) listedOffers[shownKey] = offer;
  }
  const waitingEntries = Object.entries(listedOffers)
    .sort(([a], [b]) => compareOfferScopes(a, b))
    .map(([scope, stored]) => ({
      scope,
      offer: stored,
      showing: Boolean(offer && normalizeOfferScope(scope) === normalizeOfferScope(activeScope)),
    }));
  const pendingNote = !busy && waitingEntries.length > 0 ? (
    <div
      className={coverOpen ? 'pt-generate__pending pt-generate__pending--above' : 'pt-generate__pending'}
      role="status"
    >
      <p>{PENDING_OFFER_NOTE}</p>
      <ul className="pt-generate__pending-list" data-testid="pt-pending-offers">
        {waitingEntries.map(({ scope, showing }) => (
          <li
            key={scope}
            className={showing
              ? 'pt-generate__pending-row pt-generate__pending-row--showing'
              : 'pt-generate__pending-row'}
            data-testid="pt-pending-offer"
            data-scope={scope}
            data-showing={showing ? 'true' : 'false'}
          >
            <span className="pt-generate__pending-name">
              {pendingOfferListLabel(scope, { showing })}
            </span>
            <div className="pt-generate__offer-actions">
              <button type="button" onClick={() => resumePending(scope)}>
                {PENDING_OFFER_RESUME}
              </button>
              <button type="button" onClick={() => discardScope(scope)}>
                {PENDING_OFFER_DISCARD}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <div className="pt-generate">
      {paidDialog}
      <p className="pt-generate__mode" data-testid="pt-generate-mode">
        {modeLine}
      </p>
      {fallbackCopy && (
        <p className="pt-generate__fallback" role="note">{fallbackCopy}</p>
      )}
      {generateClosed && workshopClosedCopy && (
        <p
          id="pt-generate-workshop-block"
          className="pt-generate__block"
          role="status"
          data-testid="pt-generate-workshop-block"
        >
          {workshopClosedCopy}
        </p>
      )}
      {fillsRest && !workshopClosedCopy && (
        <p className="pt-generate__fill-hint" role="note" data-testid="pt-generate-fills-rest">{fillsRest}</p>
      )}
      {warn && (
        <p className="pt-generate__thin" role="status">{warn}</p>
      )}
      {/* Runs 33-34: Notes steer only a Framework outline; without one the toggle did nothing. */}
      {hasFramework && (
        <>
          <p className="pt-generate__notes-handy">{NOTES_HANDY_COPY}</p>
          <label className="pt-generate__toggle">
            <input
              type="checkbox"
              checked={useNotes}
              onChange={(e) => onUseNotesChange(e.target.checked)}
              disabled={busy}
            />
            {USE_NOTES_LABEL}
          </label>
        </>
      )}
      {pendingNote && (coverOpen ? createPortal(pendingNote, document.body) : pendingNote)}
      {hideUntilScene && (
        <p className="pt-generate__block">{EMPTY_GRID_TOOLS_WAIT}</p>
      )}
      <div className="pt-generate__actions">
        <button
            type="button"
            className="pt-generate__btn"
            onClick={onClick}
            disabled={busy || !board.generate || generateClosed}
            aria-describedby={generateClosed && workshopClosedCopy ? 'pt-generate-workshop-block' : undefined}
            aria-label={busy ? 'Generating Plot Grid' : 'Generate Plot Grid'}
          >
            {busy ? 'Generating…' : 'Generate'}
          </button>
        {!hideUntilScene && (
          <PlotTrackCheckOutline
            storyId={storyId}
            checkOutline={board.checkOutline}
          />
        )}
        {!hideUntilScene && (
          <PlotTrackSequenceCheck storyId={storyId} runSignal={seqRunSignal} />
        )}
        {showReplace && !busy && !offer && (
          <button
            type="button"
            className="pt-generate__btn"
            onClick={() => {
              if (replaceBlocked) return;
              setReplaceIntent(true);
              beginPaid(WHOLE_OUTLINE_SCOPE, () => openCostDialog({ forReplace: true }));
            }}
            disabled={busy || replaceBlocked}
          >
            {REPLACE_EXISTING_LABEL}
          </button>
        )}
      </div>
      {busy && (
        <div className="pt-generate__wait" aria-busy="true">
          <p role="status" aria-live="polite">
            {generateProgressCopy(elapsedMs)}
          </p>
          <p className="pt-generate__eta">{generateEtaCopy(elapsedMs)}</p>
          <div
            className="pt-generate__meter"
            role="progressbar"
            aria-label="Generate progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={generateProgressPercent(elapsedMs)}
          >
            <div
              className="pt-generate__meter-fill"
              style={{ width: `${generateProgressPercent(elapsedMs)}%` }}
            />
          </div>
          <button
            type="button"
            className="pt-generate__btn"
            onClick={() => waitRef.current?.cancel()}
          >
            {GENERATE_CANCEL_LABEL}
          </button>
        </div>
      )}
      {!busy && offer && (
        <div className="pt-generate__offer" role="region" aria-label="Generated outline offer">
          {offerSummary(offer, { hasOutline: hasGrid }) && (
            <p className="pt-generate__offer-summary" data-testid="pt-offer-summary">
              {offerSummary(offer, { hasOutline: hasGrid })}
            </p>
          )}
          {offerPlaceholderCount(offer) > 0 && (
            <p className="pt-generate__offer-summary" data-testid="pt-offer-placeholders">
              {OFFER_PLACEHOLDER_COUNT(offerPlaceholderCount(offer))}
            </p>
          )}
          <p className="pt-generate__offer-mode">{offerFieldText(offer.mode_line, modeLine)}</p>
          {offerFieldText(offer.disclaimer) && (
            <p className="pt-generate__disclaimer" role="note">{offerFieldText(offer.disclaimer)}</p>
          )}
          {offerIntegrityNotes(offer).map((note) => (
            <p key={note} className="pt-generate__integrity" role="status">{note}</p>
          ))}
          {offerFightRows(offer).map((fight, fightIndex) => {
            const fightName = offerFieldText(fight.name, 'this');
            const noteText = offerFieldText(fight.note_text);
            const talkText = offerFieldText(fight.talk_text);
            return (
            <div key={`${fightName}-${fightIndex}`} className="pt-generate__fight" role="group" aria-label={`Note and chat disagree: ${fightName}`}>
              <p>{FIGHT_COPY}</p>
              <p>Note: {noteText}</p>
              <p>Chat: {talkText}</p>
              <div className="pt-generate__fight-actions">
                <button
                  type="button"
                  onClick={() => setFightChoices((prev) => ({ ...prev, [fightName]: 'note' }))}
                >
                  {FIGHT_KEEP_NOTE}
                </button>
                <button
                  type="button"
                  onClick={() => setFightChoices((prev) => ({ ...prev, [fightName]: 'talk' }))}
                >
                  {FIGHT_KEEP_TALK}
                </button>
                <button
                  type="button"
                  onClick={() => setFightChoices((prev) => ({ ...prev, [fightName]: 'edit' }))}
                >
                  {FIGHT_EDIT_LABEL}
                </button>
              </div>
              {fightChoices[fightName] === 'edit' && (
                <textarea
                  aria-label={`Edit Note for ${fightName}`}
                  value={fightEdits[fightName] ?? noteText}
                  onChange={(e) => setFightEdits((prev) => ({ ...prev, [fightName]: e.target.value }))}
                />
              )}
            </div>
            );
          })}
          <ul className="pt-generate__offer-list">
            {groupOfferScenes(scenes).map((group) => {
              const patchScene = (index, patch) => {
                const nextPlan = editOfferScene(offer?.plan, index, patch);
                keepOffer({
                  ...offer,
                  plan: nextPlan,
                  scenes: offerScenes({ plan: nextPlan }),
                }, activeScopeRef.current);
              };
              if (group.kind === 'sequence') {
                return (
                  <li
                    key={`seq-${group.act}-${group.sequence}-${group.children[0].index}`}
                    data-grain="sequence"
                    data-sequence={group.heading}
                    className={keptSequences.has(group.sequence) ? 'pt-generate__offer-kept' : undefined}
                    data-kept={keptSequences.has(group.sequence) ? 'true' : undefined}
                  >
                    <span className="pt-generate__offer-sequence">{group.heading}</span>
                    {keptSequences.has(group.sequence) && (
                      <span className="pt-generate__offer-kept-note">{OFFER_KEPT_NOTE}</span>
                    )}
                    <ul className="pt-generate__offer-scenes">
                      {group.children.map(({ scene, index }) => {
                        if (!scene || typeof scene !== 'object') return null;
                        return (
                          <li key={`stub-${index}`} data-grain="scene">
                            <OfferSceneBody
                              scene={scene}
                              index={index}
                              editing={editing}
                              nestedStub
                              onPatch={(patch) => patchScene(index, patch)}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                );
              }
              const { scene, index } = group;
              if (!scene || typeof scene !== 'object') return null;
              const sceneKept = keptSequences.has(scene.sequence);
              const showKeptNote = sceneKept && !keptNoted.has(scene.sequence);
              if (showKeptNote) keptNoted.add(scene.sequence);
              const skipReason = sceneKept ? null : skippedReasons.get(offerSkipKey(scene.sequence, scene.name));
              const leftOut = sceneKept || Boolean(skipReason);
              const sequenceName = offerSequenceName(scene);
              const showSequence = Boolean(sequenceName) && sequenceName !== lastSequence;
              if (sequenceName) lastSequence = sequenceName;
              return (
                <li
                  key={`${offerSceneHeading(scene)}-${index}`}
                  className={leftOut ? 'pt-generate__offer-kept' : undefined}
                  data-kept={leftOut ? 'true' : undefined}
                  data-grain={scene?.grain === 'sequence' ? 'sequence' : 'scene'}
                >
                  {showSequence && (
                    <span className="pt-generate__offer-sequence pt-generate__offer-sequence--above" data-testid="pt-offer-sequence-name">
                      {offerSequenceLabel(sequenceName, sequenceCounts.get(sequenceName))}
                    </span>
                  )}
                  {showKeptNote && (
                    <span className="pt-generate__offer-kept-note">{OFFER_KEPT_NOTE}</span>
                  )}
                  {skipReason && (
                    <span className="pt-generate__offer-kept-note">{`Not added — ${skipReason}`}</span>
                  )}
                  <OfferSceneBody
                    scene={scene}
                    index={index}
                    editing={editing}
                    onPatch={(patch) => patchScene(index, patch)}
                  />
                </li>
              );
            })}
          </ul>
          {hasOutline && replaceIntent && (
            <p className="pt-generate__fill-hint" role="note">{REPLACE_REVIEW_FIRST_NOTE}</p>
          )}
          {hasOutline && <p className="pt-generate__fill-hint">{FILL_EMPTY_HINT}</p>}
          <div className="pt-generate__offer-actions">
            <button type="button" onClick={() => runAccept(hasOutline ? 'fill_empty' : 'write')}>
              {offer?.entity_grounding?.dominated ? ACCEPT_ANYWAY_LABEL : acceptOfferLabel(hasOutline)}
            </button>
            {offer?.continue_available && (
              <button type="button" onClick={runContinue} disabled={generateClosed}>
                {offerFieldText(offer.continue_label, CONTINUE_INCOMPLETE_LABEL)}
              </button>
            )}
            <button type="button" onClick={() => setEditing((prev) => !prev)}>
              {OFFER_EDIT_LABEL}
            </button>
            <button
              type="button"
              onClick={() => {
                dropOffer();
                setFinishedNote(null);
              }}
            >
              {OFFER_REJECT_LABEL}
            </button>
            {showReplace && (
              <button
                type="button"
                onClick={() => {
                  if (replaceBlocked) return;
                  openReplaceConfirm(offer?.outline_prose, 'replace');
                }}
                disabled={busy || replaceBlocked}
              >
                {REPLACE_EXISTING_LABEL}
              </button>
            )}
          </div>
        </div>
      )}
      {!busy && finishedNote && !offer && (
        <p className="pt-generate__done" role="status" aria-live="polite">
          {finishedNote}
        </p>
      )}
      {!busy && finishedNote && !offer && missingLine && (
        <p className="pt-generate__done" role="note" data-testid="pt-generate-missing">
          {missingLine}
        </p>
      )}
      {!busy && finishedNote && !offer && skippedLines.length > 0 && (
        <div className="pt-generate__done" role="note" data-testid="pt-generate-skipped">
          <p>{FILL_SKIPPED_HEADING}</p>
          <ul>
            {skippedLines.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
      )}
      {disclaimer && (
        <p className="pt-generate__disclaimer" role="note">
          {disclaimer}
        </p>
      )}
      {emptyExplain && (
        <p className="pt-generate__empty" role="alert">
          {emptyExplain}
        </p>
      )}
      {error && <p className="pt-generate__error" role="alert">{error}</p>}
      {error && !busy && (
        <button type="button" className="pt-generate__btn" onClick={onRetry}>
          {GENERATE_RETRY_LABEL}
        </button>
      )}
      {planPart?.name && !busy && planReady && (
        <PlanPartConfirm
          partName={planPart.name}
          fillsSpine={readiness?.fills_spine === true}
          estimatedInputTokens={readiness?.estimated_input_tokens}
          promptChars={readiness?.prompt_chars}
          sizePending={sizePending}
          onConfirm={confirmPlanPart}
          onCancel={onPlanPartCancel}
        />
      )}
      {costOpen && !busy && (replaceIntent || !generateClosed) && (
        <PlotTrackGenerateConfirm
          fillsSpine={readiness?.fills_spine === true}
          estimatedInputTokens={readiness?.estimated_input_tokens}
          promptChars={readiness?.prompt_chars}
          sizePending={sizePending}
          onConfirm={() => {
            setCostOpen(false);
            startPreview({ fromPlan: false, replaceConfirmed: true });
          }}
          onCancel={() => setCostOpen(false)}
        />
      )}
      <ConfirmModal
        isOpen={Boolean(scopeReplace) || planWarning}
        title={PENDING_OFFER_REPLACE_TITLE}
        message={pendingOfferReplaceCopy(scopeReplace || planScope)}
        confirmLabel={PENDING_OFFER_REPLACE_CONFIRM}
        cancelLabel="Keep this"
        onConfirm={() => {
          if (scopeReplaceRef.current) finishScopeReplace(true);
          else setPlanClearedFor(planScope);
        }}
        onCancel={() => {
          if (scopeReplaceRef.current) {
            finishScopeReplace(false);
            return;
          }
          setPlanDismissed(true);
          onPlanPartCancel?.();
        }}
      />
      <ConfirmModal
        isOpen={confirmOpen && !busy && !replaceBlocked}
        title={REPLACE_CONFIRM_TITLE}
        message={[
          replaceWarning({
            rows: board.rows,
            columns: board.columns,
            headers: board.headers,
          }),
          proseLossLine(proseLoss || {}),
        ].filter(Boolean).join(' ')}
        confirmLabel="Replace"
        cancelLabel="Keep this"
        destructive
        requireText={proseLoss?.accepted ? REPLACE_TYPE_WORD : ''}
        requireLabel={REPLACE_TYPE_LABEL}
        onConfirm={() => runAccept(confirmApply, { confirmProse: Boolean(proseLoss) })}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
